/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, August 8th 2024, 7:38:00 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Paper from "@mui/material/Paper";
import TranslateIcon from "@mui/icons-material/Translate";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { formatDuration } from "date-fns";
import { isEmpty } from "lodash";
import { TLocales, TranslatableModel } from "lib/schemas";
import { formatBytes } from "lib/utils";
import { useTranslationAPI } from "translation/services";
import {
  APIItemDisplayWrapper,
  StyledOSDialog,
  OSDropDown,
  OSAlert,
  OSActionButton,
  OSFileInput,
  OSLoadingButton,
} from "ui-widgets";

type LanguageSelectorProps = {
  onSelectLanguage: (languageCode: keyof TLocales) => void;
};

export const LanguageSelector = ({ onSelectLanguage }: LanguageSelectorProps) => {
  const { useGetLanguages } = useTranslationAPI();
  const {
    locales,
    isMakingAPIRequest: isFetchingLocales,
    getSupportedLocales,
    httpResponseStatusCode: fetchLocalesStatusCode,
    isAPIError: fetchLocalesError,
  } = useGetLanguages();

  React.useEffect(() => {
    getSupportedLocales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <APIItemDisplayWrapper
      showLoader={isFetchingLocales}
      httpResponseStatusCode={fetchLocalesStatusCode}
      isAPIError={fetchLocalesError}
    >
      <OSDropDown options={locales} fullWidth label="Choose a target language" onChangeCallBack={onSelectLanguage} />
    </APIItemDisplayWrapper>
  );
};

type TranslatableModelSelectorProps = {
  onChooseModel: (model_id: string) => void;
  selectedModelId: string;
  showEmpty?: boolean;
};

export const TranslatableModelSelector = ({
  onChooseModel,
  selectedModelId,
  showEmpty,
}: TranslatableModelSelectorProps) => {
  const { useGetTranslatableModels } = useTranslationAPI();
  const { getModels } = useGetTranslatableModels();
  const [models, setModels] = React.useState<TranslatableModel[]>([]);

  React.useEffect(() => {
    getModels(setModels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ minWidth: "300px" }}>
      <OSDropDown
        value={selectedModelId}
        options={models.reduce((prev, model) => ({ ...prev, [model.id]: model.name }), showEmpty ? { "": "" } : {})}
        fullWidth
        label="Choose Model"
        onChangeCallBack={onChooseModel}
      />
    </Paper>
  );
};

type LanguageAndModelSelectorProps = LanguageSelectorProps & TranslatableModelSelectorProps;

export const LanguageAndModelSelector = (props: LanguageAndModelSelectorProps) => {
  return (
    <>
      <LanguageSelector onSelectLanguage={props.onSelectLanguage} />
      <TranslatableModelSelector
        selectedModelId={props.selectedModelId}
        onChooseModel={props.onChooseModel}
        showEmpty={props.showEmpty}
      />
      {!props.selectedModelId ? (
        <OSAlert severity="info">If you dont select a specific model, all translatable models will used.</OSAlert>
      ) : null}
    </>
  );
};

export const ExportPOFile = ({
  renderOpenButton,
}: Partial<Pick<React.ComponentProps<typeof StyledOSDialog>, "renderOpenButton">>) => {
  const { usePOFileAPI } = useTranslationAPI();

  const {
    exportFile,
    isMakingAPIRequest: isExportingPOFile,
    httpResponseStatusCode: exportStatusCode,
    isAPIError: exportHasError,
  } = usePOFileAPI();

  const [selectedLocale, setSelectedLocale] = React.useState<keyof TLocales>();
  const [selectedModelId, setSelectedModelId] = React.useState<TranslatableModel["id"]>();
  const [fileDownloadURL, setFileDownloadURL] = React.useState<string>();

  const onClickExportToFile = () => {
    exportFile(
      selectedLocale,
      (blob) => {
        // https://stackoverflow.com/questions/32545632/how-can-i-download-a-file-using-window-fetch
        const fileURL = window.URL.createObjectURL(blob);
        setFileDownloadURL(fileURL);
      },
      selectedModelId
    );
  };

  const progressLoader = selectedLocale ? (
    <APIItemDisplayWrapper
      showLoader={isExportingPOFile}
      isAPIError={exportHasError}
      httpResponseStatusCode={exportStatusCode}
    />
  ) : null;

  return (
    <StyledOSDialog
      renderOpenButton={renderOpenButton}
      popUpTitle="Export PO File"
      popUpIcon={<TranslateIcon color="primary" />}
      renderContentWithClose={({ handleClose }) => (
        <Stack spacing={2} justifyContent="center">
          <LanguageAndModelSelector
            onChooseModel={setSelectedModelId}
            selectedModelId={selectedModelId}
            showEmpty
            onSelectLanguage={setSelectedLocale}
          />
          {progressLoader}
          {fileDownloadURL ? (
            <OSAlert severity="success" alertTitle="Export was successful. Download the file using the link below.">
              <a href={fileDownloadURL} download="exported_translations.po" style={{ textAlign: "center" }}>
                Download PO Exported File
              </a>
            </OSAlert>
          ) : null}
          <OSActionButton variant="contained" disabled={!selectedLocale} onClick={onClickExportToFile}>
            Export to PO File
          </OSActionButton>
        </Stack>
      )}
    />
  );
};

const VALID_PO_MIME_TYPES = ["text/x-gettext-translation", "text/plain"];

const formatSecondsToMinutes = (secondsToFormat: number) => {
  const seconds = Math.round((secondsToFormat % 60) * 100) / 100;
  const minutes = Math.floor(secondsToFormat / 60);
  return formatDuration({ minutes, seconds }, { format: ["minutes", "seconds"] });
};

export const ImportPOFile = ({
  renderOpenButton,
}: Partial<Pick<React.ComponentProps<typeof StyledOSDialog>, "renderOpenButton">>) => {
  const { usePOFileAPI } = useTranslationAPI();

  const { importFile, isMakingAPIRequest: isImportingPOFile, apiErrors } = usePOFileAPI();

  const [selectedLocale, setSelectedLocale] = React.useState<keyof TLocales>();
  const [selectedModelId, setSelectedModelId] = React.useState<TranslatableModel["id"]>();
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>();
  const [responseState, setResponseState] =
    React.useState<{ importTranslations: Record<string, string>; secondsTaken: number }>();
  const poFile = uploadedFiles?.[0];
  const isValid = VALID_PO_MIME_TYPES.includes(poFile?.type);

  const onClickImportFile = () => {
    const startTime = performance.now();
    importFile(
      poFile,
      selectedLocale,
      (responseData) => {
        setResponseState({ importTranslations: responseData, secondsTaken: (performance.now() - startTime) / 1000 });
      },
      selectedModelId
    );
  };

  return (
    <StyledOSDialog
      renderOpenButton={renderOpenButton}
      popUpTitle="Import PO File"
      popUpIcon={<TranslateIcon color="primary" />}
      renderContentWithClose={({ handleClose }) => (
        <Stack spacing={2} justifyContent="center">
          <LanguageAndModelSelector
            onChooseModel={setSelectedModelId}
            selectedModelId={selectedModelId}
            showEmpty
            onSelectLanguage={setSelectedLocale}
          />
          <OSFileInput
            dropTargetText="Drag a valid PO file from your local machine and Drop it here to import translations"
            filePreviewProps={{
              validFileMessage: (
                <>
                  <p>{poFile?.name}</p>
                  <Typography variant="subtitle1">
                    {formatBytes(poFile?.size)} | {poFile?.type}
                  </Typography>
                </>
              ),
              invalidFileMessage: <p>{poFile?.name} is not a valid PO file.</p>,
            }}
            isValid={isValid}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
          <OSLoadingButton
            isLoading={isImportingPOFile}
            variant="contained"
            disabled={!isValid || isImportingPOFile}
            onClick={onClickImportFile}
          >
            Import Translations
          </OSLoadingButton>
          {isEmpty(apiErrors) ? null : (
            <OSAlert severity="error" alertTitle="Failed Import!">
              {JSON.stringify(apiErrors, null, 2)}
            </OSAlert>
          )}
          {responseState?.importTranslations ? (
            <OSAlert severity="success" alertTitle="Successful Import!">
              You have successfully updated {Object.keys(responseState?.importTranslations).length} translations{" "}
              {responseState?.secondsTaken ? `in ${formatSecondsToMinutes(responseState?.secondsTaken)}` : ""}
            </OSAlert>
          ) : null}
        </Stack>
      )}
    />
  );
};
